import { AIScanner } from '../scanner-ai.js';
import { 
  getNotasLocal, saveProdutoScanned, getProdutosScanned, deleteProdutoScanned, 
  saveListaItemLocal, getListasLocal, saveListaLocal, deleteListaLocal, 
  enrichPendingProdutos, getOpenShoppingLists, createQuickShoppingList,
  getFrequentStoresFromNotas, saveCotacaoLocal, getCotacoesByEAN, resolvePendingCotacoes
} from '../db.js';
import { authManager } from '../auth-manager.js';
import { categorizeItem } from '../categorizer.js';

function escapeHTML(str) {
  if (!str) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function formatAIErrorMessage(err) {
  if (err?.name === 'AbortError' || err?.message?.includes('aborted') || err?.message?.includes('AbortError')) {
    return null;
  }
  const msg = (err && (err.message || String(err))) || '';
  console.error('[AI-Scanner] Detalhes técnicos do erro:', err);

  if (msg.includes('context deadline exceeded') || msg.includes('timeout') || msg.includes('timed out')) {
    return 'Tempo de resposta excedido. Aproxime a câmera da etiqueta de preço e tente novamente.';
  }
  if (msg.includes('503') || msg.includes('high demand') || msg.includes('UNAVAILABLE') || msg.includes('temporariamente')) {
    return 'O serviço de IA está temporariamente ocupado. Tente novamente em alguns segundos.';
  }
  if (msg.includes('429') || msg.includes('quota') || msg.includes('exceeded your current quota') || msg.includes('RATE_LIMIT')) {
    return 'Limite de requisições atingido. Aguarde alguns instantes antes de escanear novamente.';
  }
  if (msg.includes('GEMINI_KEY_MISSING') || msg.includes('chave')) {
    return 'Chave de API do Gemini não configurada no servidor (.env).';
  }
  if (msg.includes('Failed to fetch') || msg.includes('NetworkError') || msg.includes('conexão')) {
    return 'Falha de conexão com o servidor. Verifique sua rede e tente novamente.';
  }
  return 'Não foi possível reconhecer a etiqueta. Tente enquadrar melhor e tirar outra foto.';
}

let activeAIScanner = null;
let currentScanMode = 'ai-label'; // 'ai-label' | '1d-barcode'
let cachedScannedProducts = [];

export function renderScannerAIView(container, param, syncManager) {
  const originHash = sessionStorage.getItem('scanner_origin_hash') || localStorage.getItem('scanner_origin_hash') || '#listas';
  let isCameraOn = localStorage.getItem('scanner_ai_camera_on') !== 'false';
  const isFromSpecificList = originHash.startsWith('#lista/') || originHash.startsWith('#lista-cotacao/');
  const targetListId = isFromSpecificList ? Number(originHash.split('/')[1]) : null;

  container.innerHTML = `
    <!-- Compact Header (Back + Title in single line) -->
    <div class="detail-header" style="display: flex; align-items: center; gap: 0.65rem; margin-bottom: 0.65rem;">
      <button type="button" id="btn-back-scanner-ai" class="detail-back" style="background: var(--bg-surface-dim); border: 1px solid var(--border-color); color: var(--text-main); cursor: pointer; width: 36px; height: 36px; border-radius: 50%; display: flex; align-items: center; justify-content: center; text-decoration: none; flex-shrink: 0;">
        <span class="material-symbols-outlined" style="font-size: 18px;">arrow_back</span>
      </button>
      <h2 style="margin: 0; font-size: 1.15rem; font-weight: 800; color: var(--text-main); line-height: 1.2;">
        ${isFromSpecificList ? 'Escanear para Lista' : 'Scanner de Produto'}
      </h2>
    </div>

    <!-- Store Session Selector (Sticky Active Market for Price Quotes) -->
    <div id="scanner-store-bar" style="margin-bottom: 0.65rem; background: var(--bg-surface-alt); border: 1px solid var(--border-color); border-radius: 0.85rem; padding: 0.55rem 0.75rem;">
      <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.35rem;">
        <div style="display: flex; align-items: center; gap: 0.35rem; font-size: 0.76rem; font-weight: 700; color: var(--text-main);">
          <span class="material-symbols-outlined" style="font-size: 16px; color: var(--color-primary-text, #2563EB);">store</span>
          <span>Cotando no estabelecimento:</span>
        </div>
        <span id="active-store-label" style="font-size: 0.74rem; font-weight: 800; color: var(--color-primary-text, #2563EB); max-width: 140px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
          Não informado
        </span>
      </div>
      <div id="quick-store-pills" style="display: flex; gap: 0.35rem; overflow-x: auto; padding-bottom: 0.15rem; scrollbar-width: none;">
        <!-- Populated dynamically with frequent stores -->
      </div>
    </div>

    <!-- Rectangular Scanner Viewport with Overlay Controls -->
    <div class="scanner-viewport-wrapper product-viewport" style="position: relative; border-radius: 1rem; overflow: hidden; background: #000; border: 1px solid var(--border-color); box-shadow: var(--shadow-card); width: 100%; aspect-ratio: 16 / 10; max-height: 230px; margin: 0 auto 0.65rem auto;">
      <div id="ai-qr-reader" class="scanner-video-container" style="width: 100%; height: 100%;"></div>

      <!-- Live Mode Instructions Banner -->
      <div id="scanner-instruction-banner" style="position: absolute; top: 8px; left: 8px; right: 8px; background: rgba(15, 23, 42, 0.75); backdrop-filter: blur(8px); border: 1px solid rgba(255,255,255,0.15); border-radius: 0.5rem; padding: 0.35rem 0.6rem; display: flex; align-items: center; justify-content: space-between; z-index: 10; pointer-events: none;">
        <div style="display: flex; align-items: center; gap: 0.35rem; font-size: 0.74rem; font-weight: 600; color: #FFFFFF;">
          <span class="material-symbols-outlined" style="font-size: 15px; color: var(--color-primary-text, #60a5fa);">crop_free</span>
          <span id="scanner-instruction-text">Enquadre a etiqueta de preço e clique em Capturar</span>
        </div>
      </div>

      <!-- Quick Camera Controls (Zoom & Torch) -->
      <div class="camera-controls-toolbar" id="camera-controls" style="position: absolute; bottom: 8px; left: 0; right: 0; display: flex; justify-content: center; align-items: center; gap: 0.5rem; z-index: 10; pointer-events: auto;">
        <button class="zoom-btn" data-zoom="1" style="background: rgba(15,23,42,0.75); border: 1px solid rgba(255,255,255,0.25); color: white; border-radius: 9999px; width: 34px; height: 34px; font-size: 0.75rem; font-weight: 700; cursor: pointer; display: flex; align-items: center; justify-content: center; backdrop-filter: blur(10px); transition: all 150ms ease;">1x</button>
        <button class="zoom-btn active" data-zoom="1.8" style="background: var(--color-primary, #2563EB); border: 1px solid var(--color-primary, #2563EB); color: #FFF; border-radius: 9999px; width: 34px; height: 34px; font-size: 0.75rem; font-weight: 700; cursor: pointer; display: flex; align-items: center; justify-content: center; backdrop-filter: blur(10px); transition: all 150ms ease;">1.8x</button>
        <button class="zoom-btn" data-zoom="2.5" style="background: rgba(15,23,42,0.75); border: 1px solid rgba(255,255,255,0.25); color: white; border-radius: 9999px; width: 34px; height: 34px; font-size: 0.75rem; font-weight: 700; cursor: pointer; display: flex; align-items: center; justify-content: center; backdrop-filter: blur(10px); transition: all 150ms ease;">2.5x</button>
        <button class="torch-btn" id="btn-torch-ai" style="background: rgba(15,23,42,0.75); border: 1px solid rgba(255,255,255,0.25); color: white; border-radius: 9999px; width: 34px; height: 34px; cursor: pointer; display: flex; align-items: center; justify-content: center; backdrop-filter: blur(10px); transition: all 150ms ease;">
          <span class="material-symbols-outlined" style="font-size: 17px;">flashlight_on</span>
        </button>
      </div>
    </div>

    <!-- Round Native-Style Camera Shutter Bar (Directly below camera) -->
    <div class="camera-shutter-bar">
      <!-- Gallery Upload Button -->
      <button type="button" class="btn-camera-gallery" id="btn-upload-ai-file" title="Escolher Foto da Galeria">
        <span class="material-symbols-outlined" style="font-size: 22px;">photo_library</span>
      </button>

      <!-- Round Camera Shutter Trigger Button -->
      <button type="button" class="btn-camera-shutter" id="btn-capture-frame" title="Tirar Foto">
        <div class="shutter-inner-circle">
          <span class="material-symbols-outlined" style="font-size: 26px;">photo_camera</span>
        </div>
      </button>

      <!-- Toggle Camera On/Off Button (Manual / Power Mode) -->
      <button type="button" class="btn-camera-gallery" id="btn-toggle-camera-power" title="${isCameraOn ? 'Desligar Câmera' : 'Ligar Câmera'}" style="background: var(--bg-surface-alt); border: 1px solid var(--border-color); color: var(--text-main); width: 44px; height: 44px; border-radius: 50%; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.15s ease;">
        <span class="material-symbols-outlined" id="icon-camera-power" style="font-size: 22px; color: ${isCameraOn ? '#10B981' : '#EF4444'};">${isCameraOn ? 'videocam' : 'videocam_off'}</span>
      </button>
    </div>

    <!-- Mode Selector Tabs (Etiqueta | Cód. Barras) — abaixo do disparador -->
    <div id="ai-mode-tabs" class="home-scope-switcher" style="margin-bottom: 0.85rem; width: 100%; box-sizing: border-box;">
      <button id="tab-mode-ai" class="home-scope-pill active" style="flex: 1; text-align: center; justify-content: center; font-size: 0.8rem; padding: 0.45rem 0.6rem; display: inline-flex; align-items: center; gap: 0.3rem;">
        <span class="material-symbols-outlined" style="font-size: 16px;">label</span>
        <span>Etiqueta</span>
        <span class="badge-pro" style="margin-left: 0.2rem;"><span class="material-symbols-outlined">diamond</span>PRO</span>
      </button>
      <button id="tab-mode-1d" class="home-scope-pill" style="flex: 1; text-align: center; justify-content: center; font-size: 0.8rem; padding: 0.45rem 0.6rem; display: inline-flex; align-items: center; gap: 0.3rem;">
        <span class="material-symbols-outlined" style="font-size: 16px;">barcode_scanner</span>
        <span>Cód. Barras</span>
      </button>
    </div>

    <input type="file" id="ai-camera-input" accept="image/*" capture="environment" style="display: none;" />
    <input type="file" id="ai-gallery-input" accept="image/*" style="display: none;" />

    <!-- Scanned Products History List in DB -->
    <div class="chart-card" style="width: 100%; padding: 1.25rem; box-sizing: border-box; margin-bottom: 2rem;">
      <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 1rem;">
        <div style="display: flex; align-items: center; gap: 0.5rem;">
          <span class="material-symbols-outlined" style="font-size: 22px; color: var(--color-primary-text, #2563EB);">receipt_long</span>
          <h3 style="margin: 0; font-size: 1.05rem; font-weight: 800; color: var(--text-main);">
            Últimos Escaneados
          </h3>
        </div>
        <span id="badge-ai-scanned-count" style="background: var(--color-primary-bg, #EFF6FF); border: 1px solid var(--color-primary-border, #BFDBFE); color: var(--color-primary-text, #2563EB); font-size: 0.75rem; font-weight: 700; padding: 0.2rem 0.6rem; border-radius: var(--radius-pill);">
          0 produtos
        </span>
      </div>

      <div id="ai-scanned-products-list" style="display: flex; flex-direction: column; gap: 0.65rem;">
        <!-- Populated dynamically (Top 10) -->
      </div>

      <div id="ai-scanned-catalog-link-container" style="display: none; margin-top: 0.85rem; padding-top: 0.75rem; border-top: 1px solid var(--border-color); text-align: center;">
        <a href="#produtos" style="display: inline-flex; align-items: center; gap: 0.35rem; font-size: 0.84rem; font-weight: 700; color: var(--color-primary-text, #2563EB); text-decoration: none;">
          <span>Ver todos os produtos no Catálogo</span>
          <span class="material-symbols-outlined" style="font-size: 16px;">arrow_forward</span>
        </a>
      </div>

      <div style="margin-top: 0.85rem; padding: 0.55rem 0.75rem; border-radius: 0.65rem; background: var(--bg-surface-alt); border: 1px dashed var(--border-color); color: var(--text-muted); font-size: 0.73rem; display: flex; align-items: center; justify-content: center; gap: 0.35rem; text-align: center;">
        <span class="material-symbols-outlined" style="font-size: 15px; color: var(--color-primary-text, #2563EB); opacity: 0.8;">group_work</span>
        <span>Base colaborativa: produtos escaneados e enriquecidos com IA são compartilhados com todos.</span>
      </div>
    </div>

    <!-- Progress / Loading Spinner Overlay -->
    <div id="ai-progress-overlay" class="modal-overlay hidden" style="position: fixed; inset: 0; background: rgba(15, 23, 42, 0.78); backdrop-filter: blur(10px); display: flex; flex-direction: column; align-items: center; justify-content: center; z-index: 1000; padding: 1.5rem; text-align: center;">
      <div style="position: relative; width: 64px; height: 64px; margin-bottom: 1rem;">
        <div style="position: absolute; inset: 0; border: 4px solid rgba(255,255,255,0.2); border-top-color: #F59E0B; border-radius: 50%; animation: spin 1s linear infinite;"></div>
        <div style="position: absolute; inset: 8px; display: flex; align-items: center; justify-content: center; color: #F59E0B;">
          <span class="material-symbols-outlined" style="font-size: 26px;">photo_camera</span>
        </div>
      </div>
      <h3 id="ai-progress-title" style="font-size: 1.15rem; font-weight: 800; color: #FFFFFF; margin: 0 0 0.25rem 0;">
        Identificando Produto...
      </h3>
      <p id="ai-progress-desc" style="font-size: 0.84rem; color: #E2E8F0; margin: 0 0 1.25rem 0; max-width: 280px; line-height: 1.45;">
        Lendo informações da etiqueta...
      </p>
      <button type="button" id="btn-cancel-ai-progress" style="background: rgba(255, 255, 255, 0.15); border: 1px solid rgba(255, 255, 255, 0.3); color: #FFFFFF; padding: 0.5rem 1.25rem; border-radius: 9999px; font-size: 0.84rem; font-weight: 700; cursor: pointer; display: inline-flex; align-items: center; gap: 0.35rem; backdrop-filter: blur(6px); transition: all 150ms ease; box-shadow: 0 4px 14px rgba(0,0,0,0.25);">
        <span class="material-symbols-outlined" style="font-size: 17px;">close</span>
        <span>Cancelar</span>
      </button>
    </div>

    <!-- Product Scan Result Modal -->
    <div id="ai-result-modal" class="modal-overlay hidden" style="position: fixed; inset: 0; background: rgba(0, 0, 0, 0.65); backdrop-filter: blur(8px); display: flex; align-items: center; justify-content: center; z-index: 1000; padding: 1rem; box-sizing: border-box;">
      <div class="product-modal-card modal-card" style="background: var(--bg-surface); border: 1px solid var(--border-color); border-radius: 1.25rem; padding: 1.4rem; width: 100%; max-width: 480px; box-shadow: var(--shadow-card); box-sizing: border-box; max-height: 90vh; overflow-y: auto;">
        
        <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 1rem;">
          <div>
            <div style="display: inline-flex; align-items: center; gap: 0.35rem; background: var(--color-primary-bg, #EFF6FF); border: 1px solid var(--color-primary-border, #BFDBFE); color: var(--color-primary-text, #2563EB); padding: 0.2rem 0.6rem; border-radius: var(--radius-pill); font-size: 0.72rem; font-weight: 700; margin-bottom: 0.35rem;">
              <span class="material-symbols-outlined" style="font-size: 14px;">check_circle</span>
              <span>Produto Identificado</span>
            </div>
          </div>
          <button id="btn-close-ai-modal" type="button" style="background: var(--bg-surface-dim); border: 1px solid var(--border-color); color: var(--text-muted); border-radius: 50%; width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; cursor: pointer;">
            <span class="material-symbols-outlined" style="font-size: 18px;">close</span>
          </button>
        </div>

        <!-- Interactive Form Fields -->
        <form id="form-ai-scanned" style="display: flex; flex-direction: column; gap: 0.85rem; margin-bottom: 1.1rem;">
          
          <!-- Field 1: Description -->
          <div>
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.3rem;">
              <label for="input-ai-desc" style="font-size: 0.75rem; color: var(--text-muted); font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em;">
                Nome do Produto
              </label>
              <button type="button" id="btn-toggle-raw-desc" style="background: none; border: none; font-size: 0.72rem; color: var(--color-primary-text, #2563EB); text-decoration: underline; cursor: pointer; padding: 0;">
                Ver texto original
              </button>
            </div>
            <input type="text" id="input-ai-desc" placeholder="Ex: Sabão Líquido Omo 3L" style="width: 100%; font-size: 0.95rem; font-weight: 700; background: var(--bg-surface-alt); border: 1px solid var(--border-color); color: var(--text-main); border-radius: 0.65rem; padding: 0.65rem 0.85rem; box-sizing: border-box; outline: none; font-family: var(--font-body);" />
            <div id="ai-raw-desc-container" style="display: none; margin-top: 0.35rem; padding: 0.4rem 0.6rem; background: var(--bg-surface-dim); border-radius: 0.4rem; border: 1px dashed var(--border-color); font-size: 0.74rem; color: var(--text-muted);">
              <strong>Texto Lido na Etiqueta:</strong> <span id="text-ai-raw-desc">--</span>
            </div>
          </div>

          <!-- Field 2: Price -->
          <div style="background: var(--bg-surface-alt); border: 1px solid var(--border-color); border-radius: 0.85rem; padding: 0.85rem;">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.3rem;">
              <label for="input-ai-price" style="font-size: 0.78rem; color: var(--text-main); font-weight: 800; text-transform: uppercase; letter-spacing: 0.05em;">
                Preço (R$)
              </label>
              <span id="label-price-source" class="text-muted" style="font-size: 0.7rem;">Identificado na Etiqueta</span>
            </div>
            <div style="position: relative; display: flex; align-items: center;">
              <span style="position: absolute; left: 0.85rem; font-size: 1.25rem; font-weight: 800; color: #10B981;">R$</span>
              <input type="text" id="input-ai-price" inputmode="decimal" placeholder="0,00" style="width: 100%; font-size: 1.4rem; font-weight: 900; color: #10B981; background: var(--bg-surface); border: 1px solid var(--border-color); border-radius: 0.65rem; padding: 0.55rem 0.85rem 0.55rem 2.6rem; box-sizing: border-box; outline: none; font-family: var(--font-mono);" />
            </div>
          </div>

          <!-- Field 3: EAN & Quantity (2 columns) -->
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 0.6rem;">
            <div>
              <label for="input-ai-ean" style="display: block; font-size: 0.74rem; color: var(--text-muted); font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 0.3rem;">
                Código de Barras
              </label>
              <input type="text" id="input-ai-ean" placeholder="Ex: 7891000100103" style="width: 100%; font-family: var(--font-mono); font-size: 0.85rem; font-weight: 700; background: var(--bg-surface-alt); border: 1px solid var(--border-color); color: var(--text-main); border-radius: 0.65rem; padding: 0.55rem 0.75rem; box-sizing: border-box; outline: none;" />
            </div>

            <div>
              <label for="input-ai-qty" style="display: block; font-size: 0.74rem; color: var(--text-muted); font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 0.3rem;">
                Unidade / Peso
              </label>
              <input type="text" id="input-ai-qty" placeholder="Ex: 130g, 1L, 8un" style="width: 100%; font-size: 0.85rem; font-weight: 600; background: var(--bg-surface-alt); border: 1px solid var(--border-color); color: var(--text-main); border-radius: 0.65rem; padding: 0.55rem 0.75rem; box-sizing: border-box; outline: none;" />
            </div>
          </div>
        </form>

        <!-- Regional Comparison Box ("Aqui vs. Concorrentes da Região") -->
        <div id="ai-comparison-box" style="background: var(--bg-surface-alt); border: 1px solid var(--border-color); border-radius: 0.85rem; padding: 0.95rem; margin-bottom: 0.85rem; display: none;">
          <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.5rem;">
            <div style="display: flex; align-items: center; gap: 0.4rem; font-size: 0.82rem; font-weight: 800; color: var(--text-main);">
              <span class="material-symbols-outlined" style="font-size: 18px; color: #10B981;">storefront</span>
              <span>Comparativo Regional (Ao Vivo)</span>
            </div>
            <span id="ai-comparison-badge" style="font-size: 0.72rem; color: var(--text-muted); font-weight: 600;"></span>
          </div>
          <div id="ai-comparison-content">
            <p class="text-muted" style="font-size: 0.8rem; margin: 0;">Consultando cotações da região...</p>
          </div>
        </div>

        <!-- Reactive History Price Match Card -->
        <div id="ai-history-box" style="background: var(--bg-surface-alt); border: 1px solid var(--border-color); border-radius: 0.85rem; padding: 0.95rem; margin-bottom: 1.25rem;">
          <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.5rem;">
            <div style="display: flex; align-items: center; gap: 0.4rem; font-size: 0.82rem; font-weight: 800; color: var(--text-main);">
              <span class="material-symbols-outlined" style="font-size: 18px; color: var(--color-primary-text, #2563EB);">history</span>
              Histórico no NFC-e Tracker
            </div>
            <span id="ai-history-badge-count" style="font-size: 0.72rem; color: var(--text-muted); font-weight: 600;"></span>
          </div>
          <div id="ai-history-content">
            <p class="text-muted" style="font-size: 0.8rem; margin: 0;">Consultando compras anteriores...</p>
          </div>
        </div>

        <!-- Action Buttons in Modal -->
        <div style="display: flex; gap: 0.75rem; flex-direction: column;">
          
          <!-- Primary Action: Guardar cotação e continuar -->
          <button id="btn-save-ai-next" type="button" class="btn btn-primary" style="padding: 0.8rem 1rem; font-weight: 800; font-size: 0.92rem; display: flex; align-items: center; justify-content: center; gap: 0.45rem; border-radius: var(--radius-pill); cursor: pointer; width: 100%;">
            <span class="material-symbols-outlined" style="font-size: 20px;">save</span>
            Guardar Cotação e Continuar
          </button>

          <!-- List Open Shopping Lists Section -->
          <div id="add-to-list-section" style="background: var(--bg-surface-alt); border: 1px solid var(--border-color); border-radius: 0.85rem; padding: 0.85rem; display: flex; flex-direction: column; gap: 0.6rem;">
            <div style="display: flex; align-items: center; justify-content: space-between;">
              <span style="font-size: 0.76rem; color: var(--text-muted); font-weight: 800; text-transform: uppercase; letter-spacing: 0.05em; display: flex; align-items: center; gap: 0.35rem;">
                <span class="material-symbols-outlined" style="font-size: 16px; color: var(--color-primary-text, #2563EB);">checklist</span>
                Adicionar à Lista de Compras
              </span>
              <span id="open-lists-count-badge" style="font-size: 0.7rem; font-weight: 700; color: var(--text-muted);"></span>
            </div>

            <!-- Scrollable list of open shopping lists -->
            <div id="open-lists-container" style="display: flex; flex-direction: column; gap: 0.45rem; max-height: 230px; overflow-y: auto; padding-right: 2px;">
              <div style="text-align: center; padding: 0.75rem; color: var(--text-muted); font-size: 0.8rem;">
                Carregando listas abertas...
              </div>
            </div>
          </div>

          <!-- Bottom Action: Voltar -->
          <button id="btn-cancel-ai-modal" type="button" class="btn btn-secondary" style="width: 100%; padding: 0.7rem 1rem; font-size: 0.85rem; border-radius: var(--radius-pill); font-weight: 700; display: flex; align-items: center; justify-content: center; gap: 0.35rem; cursor: pointer;">
            <span class="material-symbols-outlined" style="font-size: 18px;">arrow_back</span>
            Voltar
          </button>
        </div>

      </div>
    </div>

    <!-- Quick Add To Shopping List Modal -->
    <div id="ai-quick-add-list-modal" class="modal-overlay hidden" style="position: fixed; inset: 0; background: rgba(0, 0, 0, 0.65); backdrop-filter: blur(8px); display: flex; align-items: center; justify-content: center; z-index: 1005; padding: 1rem; box-sizing: border-box;">
      <div class="modal-card" style="background: var(--bg-surface); border: 1px solid var(--border-color); border-radius: 1.25rem; padding: 1.4rem; width: 100%; max-width: 440px; box-shadow: var(--shadow-card); box-sizing: border-box;">
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.85rem;">
          <h4 style="margin: 0; font-size: 1.05rem; font-weight: 800; color: var(--text-main); display: flex; align-items: center; gap: 0.4rem;">
            <span class="material-symbols-outlined" style="color: var(--color-primary-text, #2563EB); font-size: 22px;">add_shopping_cart</span>
            Adicionar à Lista
          </h4>
          <button id="btn-close-quick-list-modal" type="button" style="background: var(--bg-surface-dim); border: 1px solid var(--border-color); color: var(--text-muted); border-radius: 50%; width: 30px; height: 30px; display: flex; align-items: center; justify-content: center; cursor: pointer;">
            <span class="material-symbols-outlined" style="font-size: 18px;">close</span>
          </button>
        </div>

        <div style="padding: 0.65rem 0.85rem; background: var(--bg-surface-alt); border-radius: 0.65rem; border: 1px solid var(--border-color); margin-bottom: 1rem;">
          <div id="quick-list-item-title" style="font-size: 0.88rem; font-weight: 800; color: var(--text-main); line-height: 1.35; margin-bottom: 0.25rem;"></div>
          <div id="quick-list-item-price" style="font-size: 0.82rem; font-weight: 700; color: #10B981; font-family: var(--font-mono);"></div>
        </div>
        
        <div style="margin-bottom: 1.15rem;">
          <label style="display: block; font-size: 0.74rem; font-weight: 700; color: var(--text-muted); text-transform: uppercase; margin-bottom: 0.45rem;">Quantidade</label>
          
          <!-- Stepper Touch-Friendly Controls -->
          <div style="display: flex; align-items: center; justify-content: center; gap: 0.65rem; margin-bottom: 0.65rem; width: 100%; box-sizing: border-box;">
            <button type="button" id="btn-quick-ai-qty-minus" aria-label="Diminuir quantidade" style="width: 48px; height: 44px; min-width: 48px; border-radius: 0.65rem; font-size: 1.4rem; font-weight: 800; border: 1px solid var(--border-color); background: var(--bg-surface-alt); color: var(--text-main); display: inline-flex; align-items: center; justify-content: center; cursor: pointer; user-select: none; transition: all 0.15s ease;">
              −
            </button>
            <input type="number" id="quick-list-item-qty" value="1" min="0.1" step="any" inputmode="decimal" style="flex: 1; max-width: 130px; height: 44px; text-align: center; font-size: 1.25rem; font-weight: 800; border-radius: 0.65rem; border: 1px solid var(--border-color); background: var(--bg-surface-dim, var(--bg-surface-alt)); color: var(--text-main); outline: none; font-family: var(--font-mono); box-sizing: border-box;" />
            <button type="button" id="btn-quick-ai-qty-plus" aria-label="Aumentar quantidade" style="width: 48px; height: 44px; min-width: 48px; border-radius: 0.65rem; font-size: 1.4rem; font-weight: 800; border: 1px solid var(--border-color); background: var(--bg-surface-alt); color: var(--text-main); display: inline-flex; align-items: center; justify-content: center; cursor: pointer; user-select: none; transition: all 0.15s ease;">
              +
            </button>
          </div>

          <!-- Quick Increment & Weight Shortcuts -->
          <div style="display: flex; gap: 0.35rem; justify-content: center; flex-wrap: wrap;">
            <button type="button" class="btn-quick-ai-qty-preset" data-delta="1" style="background: var(--bg-surface-dim); border: 1px solid var(--border-color); color: var(--text-main); border-radius: var(--radius-pill); font-size: 0.76rem; font-weight: 700; padding: 0.3rem 0.65rem; cursor: pointer; transition: all 0.15s ease;">+1</button>
            <button type="button" class="btn-quick-ai-qty-preset" data-delta="2" style="background: var(--bg-surface-dim); border: 1px solid var(--border-color); color: var(--text-main); border-radius: var(--radius-pill); font-size: 0.76rem; font-weight: 700; padding: 0.3rem 0.65rem; cursor: pointer; transition: all 0.15s ease;">+2</button>
            <button type="button" class="btn-quick-ai-qty-preset" data-delta="5" style="background: var(--bg-surface-dim); border: 1px solid var(--border-color); color: var(--text-main); border-radius: var(--radius-pill); font-size: 0.76rem; font-weight: 700; padding: 0.3rem 0.65rem; cursor: pointer; transition: all 0.15s ease;">+5</button>
            <button type="button" class="btn-quick-ai-qty-preset" data-set="0.5" style="background: var(--bg-surface-dim); border: 1px solid var(--border-color); color: var(--text-muted); border-radius: var(--radius-pill); font-size: 0.76rem; font-weight: 700; padding: 0.3rem 0.65rem; cursor: pointer; transition: all 0.15s ease;">0.5 kg</button>
            <button type="button" class="btn-quick-ai-qty-preset" data-set="1" style="background: var(--bg-surface-dim); border: 1px solid var(--border-color); color: var(--text-muted); border-radius: var(--radius-pill); font-size: 0.76rem; font-weight: 700; padding: 0.3rem 0.65rem; cursor: pointer; transition: all 0.15s ease;">Reset (1)</button>
          </div>
        </div>

        <label style="display: block; font-size: 0.74rem; font-weight: 700; color: var(--text-muted); text-transform: uppercase; margin-bottom: 0.45rem;">Selecione a Lista de Destino</label>
        <div id="quick-list-options-container" style="display: flex; flex-direction: column; gap: 0.5rem; max-height: 200px; overflow-y: auto; margin-bottom: 1.15rem;">
          <!-- Populated dynamically -->
        </div>

        <button id="btn-cancel-quick-list-modal" type="button" class="btn btn-secondary" style="width: 100%; padding: 0.65rem; border-radius: var(--radius-pill); font-weight: 700; cursor: pointer;">
          Cancelar
        </button>
      </div>
    </div>

    <!-- Modal: Informar Estabelecimento Customizado (+ Outro) -->
    <div id="modal-custom-scanner-store" class="modal-overlay hidden" style="position: fixed; inset: 0; background: rgba(0, 0, 0, 0.65); backdrop-filter: blur(8px); display: flex; align-items: center; justify-content: center; z-index: 1007; padding: 1rem; box-sizing: border-box;">
      <div class="modal-card" style="background: var(--bg-surface); border: 1px solid var(--border-color); border-radius: 1.25rem; padding: 1.4rem; width: 100%; max-width: 420px; box-shadow: var(--shadow-card); box-sizing: border-box;">
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.85rem;">
          <h4 style="margin: 0; font-size: 1.05rem; font-weight: 800; color: var(--text-main); display: flex; align-items: center; gap: 0.4rem;">
            <span class="material-symbols-outlined" style="color: var(--color-primary-text, #2563EB); font-size: 22px;">storefront</span>
            Informar Estabelecimento
          </h4>
          <button id="btn-close-custom-store-modal" type="button" style="background: var(--bg-surface-dim); border: 1px solid var(--border-color); color: var(--text-muted); border-radius: 50%; width: 30px; height: 30px; display: flex; align-items: center; justify-content: center; cursor: pointer;">
            <span class="material-symbols-outlined" style="font-size: 18px;">close</span>
          </button>
        </div>

        <p style="font-size: 0.82rem; color: var(--text-muted); margin: 0 0 1rem 0;">
          Digite o nome do supermercado, atacado ou loja onde você está realizando as cotações:
        </p>

        <div style="margin-bottom: 1.25rem;">
          <label for="input-custom-scanner-store-name" style="display: block; font-size: 0.74rem; font-weight: 700; color: var(--text-muted); text-transform: uppercase; margin-bottom: 0.35rem;">Nome do Supermercado / Loja</label>
          <input type="text" id="input-custom-scanner-store-name" placeholder="Ex: Guanabara Tijuca, Assaí, Carrefour..." style="width: 100%; padding: 0.65rem 0.85rem; border: 1px solid var(--border-color); border-radius: 0.65rem; background: var(--bg-surface-alt); color: var(--text-main); font-weight: 700; font-size: 0.95rem; box-sizing: border-box; outline: none;" />
        </div>

        <div style="display: flex; gap: 0.5rem;">
          <button id="btn-confirm-custom-store" type="button" class="btn btn-primary" style="flex: 1; padding: 0.65rem; border-radius: var(--radius-pill); font-weight: 800; cursor: pointer;">
            Definir Local
          </button>
          <button id="btn-cancel-custom-store-modal" type="button" class="btn btn-secondary" style="padding: 0.65rem 1rem; border-radius: var(--radius-pill); font-weight: 700; cursor: pointer;">
            Cancelar
          </button>
        </div>
      </div>
    </div>
  `;

  // Initialize Elements & State
  const tabModeAI = container.querySelector('#tab-mode-ai');
  const tabMode1D = container.querySelector('#tab-mode-1d');
  const btnCaptureFrame = container.querySelector('#btn-capture-frame');
  const btnUploadFile = container.querySelector('#btn-upload-ai-file');
  const cameraInput = container.querySelector('#ai-camera-input');
  const galleryInput = container.querySelector('#ai-gallery-input');
  const progressOverlay = container.querySelector('#ai-progress-overlay');
  const progressTitle = container.querySelector('#ai-progress-title');
  const progressDesc = container.querySelector('#ai-progress-desc');
  const resultModal = container.querySelector('#ai-result-modal');
  const btnCloseModal = container.querySelector('#btn-close-ai-modal');
  const btnCancelModal = container.querySelector('#btn-cancel-ai-modal');
  const btnSaveNext = container.querySelector('#btn-save-ai-next');
  const inputEan = container.querySelector('#input-ai-ean');
  const inputDesc = container.querySelector('#input-ai-desc');
  const inputPrice = container.querySelector('#input-ai-price');
  const inputQty = container.querySelector('#input-ai-qty');
  const textRawDesc = container.querySelector('#text-ai-raw-desc');
  const rawDescContainer = container.querySelector('#ai-raw-desc-container');
  const btnToggleRawDesc = container.querySelector('#btn-toggle-raw-desc');
  const historyContent = container.querySelector('#ai-history-content');
  const historyBadge = container.querySelector('#ai-history-badge-count');
  const comparisonBox = container.querySelector('#ai-comparison-box');
  const comparisonBadge = container.querySelector('#ai-comparison-badge');
  const comparisonContent = container.querySelector('#ai-comparison-content');

  let currentRawDesc = '';
  let activeHistoryMatches = [];
  let activeRegionalQuotes = [];
  let currentIsPrecoSugerido = false;

  let activeSessionStore = localStorage.getItem('nfce_active_scanner_store') || '';
  let sessionScannedCount = 0;
  let sessionPendingCount = 0;

  // Helper: Format BRL Currency
  function formatBRL(value) {
    if (typeof value !== 'number' || isNaN(value)) return 'R$ 0,00';
    return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }

  // Render quick store selector pills
  async function renderStorePills() {
    const pillsContainer = container.querySelector('#quick-store-pills');
    const activeLabel = container.querySelector('#active-store-label');
    if (!pillsContainer) return;

    if (activeLabel) {
      activeLabel.textContent = activeSessionStore || 'Não informado';
      activeLabel.style.color = activeSessionStore ? 'var(--color-primary-text, #2563EB)' : 'var(--text-muted)';
    }

    const frequentStores = await getFrequentStoresFromNotas();
    const topStores = frequentStores.slice(0, 5);

    let html = `
      <button type="button" class="store-pill ${!activeSessionStore ? 'active' : ''}" data-store="" style="padding: 0.28rem 0.65rem; border-radius: var(--radius-pill); font-size: 0.74rem; font-weight: 700; border: 1px solid ${!activeSessionStore ? 'var(--color-primary, #2563EB)' : 'var(--border-color)'}; background: ${!activeSessionStore ? 'var(--color-primary, #2563EB)' : 'var(--bg-surface)'}; color: ${!activeSessionStore ? '#FFF' : 'var(--text-muted)'}; cursor: pointer; white-space: nowrap; display: inline-flex; align-items: center; gap: 0.25rem;">
        <span class="material-symbols-outlined" style="font-size: 13px;">help_outline</span>
        <span>Não informado</span>
      </button>
    `;

    for (const st of topStores) {
      const isActive = activeSessionStore === st;
      html += `
        <button type="button" class="store-pill ${isActive ? 'active' : ''}" data-store="${escapeHTML(st)}" style="padding: 0.28rem 0.65rem; border-radius: var(--radius-pill); font-size: 0.74rem; font-weight: 700; border: 1px solid ${isActive ? 'var(--color-primary, #2563EB)' : 'var(--border-color)'}; background: ${isActive ? 'var(--color-primary, #2563EB)' : 'var(--bg-surface)'}; color: ${isActive ? '#FFF' : 'var(--text-main)'}; cursor: pointer; white-space: nowrap; display: inline-flex; align-items: center; gap: 0.25rem;">
          <span class="material-symbols-outlined" style="font-size: 13px;">shopping_cart</span>
          <span>${escapeHTML(st)}</span>
        </button>
      `;
    }

    html += `
      <button type="button" id="btn-custom-store" style="padding: 0.28rem 0.65rem; border-radius: var(--radius-pill); font-size: 0.74rem; font-weight: 700; border: 1px dashed var(--border-color); background: var(--bg-surface); color: var(--color-primary-text, #2563EB); cursor: pointer; white-space: nowrap; display: inline-flex; align-items: center; gap: 0.25rem;">
        <span class="material-symbols-outlined" style="font-size: 13px;">add</span>
        <span>Outro</span>
      </button>
    `;

    pillsContainer.innerHTML = html;

    pillsContainer.querySelectorAll('.store-pill').forEach(btn => {
      btn.addEventListener('click', () => {
        const store = btn.dataset.store || '';
        setSessionStore(store);
      });
    });

    const btnCustom = pillsContainer.querySelector('#btn-custom-store');
    if (btnCustom) {
      btnCustom.addEventListener('click', openCustomStoreModal);
    }
  }

  // Custom Store Modal Controller
  const modalCustomStore = container.querySelector('#modal-custom-scanner-store');
  const inputCustomStoreName = container.querySelector('#input-custom-scanner-store-name');
  const btnCloseCustomStore = container.querySelector('#btn-close-custom-store-modal');
  const btnCancelCustomStore = container.querySelector('#btn-cancel-custom-store-modal');
  const btnConfirmCustomStore = container.querySelector('#btn-confirm-custom-store');

  function openCustomStoreModal() {
    if (inputCustomStoreName) {
      inputCustomStoreName.value = activeSessionStore || '';
      setTimeout(() => inputCustomStoreName.focus(), 80);
    }
    modalCustomStore?.classList.remove('hidden');
  }

  function closeCustomStoreModal() {
    modalCustomStore?.classList.add('hidden');
  }

  if (btnCloseCustomStore) btnCloseCustomStore.addEventListener('click', closeCustomStoreModal);
  if (btnCancelCustomStore) btnCancelCustomStore.addEventListener('click', closeCustomStoreModal);

  if (btnConfirmCustomStore) {
    btnConfirmCustomStore.addEventListener('click', () => {
      const val = inputCustomStoreName?.value?.trim();
      if (val) {
        setSessionStore(val);
        closeCustomStoreModal();
      } else {
        syncManager.showToast('Informe o nome do estabelecimento.', 'warning');
        inputCustomStoreName?.focus();
      }
    });
  }

  if (inputCustomStoreName) {
    inputCustomStoreName.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        btnConfirmCustomStore?.click();
      }
    });
  }

  function setSessionStore(store) {
    activeSessionStore = store;
    localStorage.setItem('nfce_active_scanner_store', store);
    renderStorePills();
    syncManager.showToast(store ? `Estabelecimento ativo: ${store}` : 'Modo sem estabelecimento definido', 'info');
  }

  // Helper: Show/Hide Progress Overlay
  function showProgress(title, desc) {
    progressTitle.textContent = title;
    progressDesc.textContent = desc;
    progressOverlay.classList.remove('hidden');
  }

  function hideProgress() {
    progressOverlay.classList.add('hidden');
  }

  // Load Saved Products List (Top 10 recent items)
  async function refreshScannedList() {
    try {
      const allScanned = await getProdutosScanned();
      cachedScannedProducts = allScanned || [];
      const listContainer = container.querySelector('#ai-scanned-products-list');
      const badgeCount = container.querySelector('#badge-ai-scanned-count');
      const catalogLinkContainer = container.querySelector('#ai-scanned-catalog-link-container');

      badgeCount.textContent = `${cachedScannedProducts.length} produtos`;

      if (cachedScannedProducts.length === 0) {
        listContainer.innerHTML = `
          <div style="text-align: center; padding: 1.5rem; color: var(--text-muted); font-size: 0.82rem;">
            <span class="material-symbols-outlined" style="font-size: 32px; opacity: 0.4; display: block; margin-bottom: 0.35rem;">qr_code_scanner</span>
            Nenhum produto escaneado ainda.
          </div>
        `;
        if (catalogLinkContainer) catalogLinkContainer.style.display = 'none';
        return;
      }

      if (catalogLinkContainer) {
        catalogLinkContainer.style.display = 'block';
        const linkSpan = catalogLinkContainer.querySelector('span');
        if (linkSpan) linkSpan.textContent = `Ver todos os produtos no Catálogo (${cachedScannedProducts.length})`;
      }

      const top10 = cachedScannedProducts.slice(0, 10);

      listContainer.innerHTML = top10.map(prod => {
        const displayPrice = typeof prod.rawPrecoGondola === 'number'
          ? prod.rawPrecoGondola
          : (typeof prod.preco === 'number' ? prod.preco : (parseFloat(prod.precoGondola?.replace(/[^\d,]/g, '').replace(',', '.')) || 0));

        const isEnriched = prod.status === 'enriquecido' && prod.descricao && prod.descricao !== 'Produto sem descrição';

        return `
        <div class="scanned-ai-card" data-id="${prod.id}" style="padding: 0.85rem 1rem; border-radius: 0.85rem; display: flex; flex-direction: column; gap: 0.45rem; background: var(--bg-surface-alt); border: 1px solid var(--border-color); cursor: pointer; transition: all 150ms ease;">
          
          <!-- Row 1: Header (EAN + Sparkle/Pendente on left, Price on right) -->
          <div style="display: flex; justify-content: space-between; align-items: center; width: 100%;">
            <div style="display: flex; align-items: center; gap: 0.35rem;">
              <span style="font-size: 0.68rem; font-family: var(--font-mono); font-weight: 700; color: var(--text-main); background: var(--bg-surface-dim); padding: 0.1rem 0.35rem; border-radius: 0.25rem; white-space: nowrap;">
                ${prod.barcode || prod.ean || 'SEM CÓDIGO'}
              </span>
              ${isEnriched ? `
                <span title="Identificado por IA" style="font-size: 0.85rem; line-height: 1; display: inline-flex; align-items: center; margin-left: 0.1rem;">✨</span>
              ` : `
                <span style="font-size: 0.65rem; background: var(--color-warning-bg, #FFFBEB); border: 1px solid var(--color-warning-border, #FDE68A); color: var(--color-warning-text, #92400E); font-weight: 700; padding: 0.08rem 0.35rem; border-radius: var(--radius-pill); display: inline-flex; align-items: center; gap: 0.15rem; white-space: nowrap;">
                  ⏳ Pendente
                </span>
              `}
              ${prod.peso_ou_quantidade ? `<span style="font-size: 0.68rem; color: var(--text-muted); white-space: nowrap;">• ${prod.peso_ou_quantidade}</span>` : ''}
            </div>

            <div style="text-align: right; display: flex; align-items: center; gap: 0.35rem;">
              <span style="font-size: 1rem; font-weight: 800; color: #10B981; font-family: var(--font-mono); white-space: nowrap;">
                ${formatBRL(displayPrice)}
              </span>
              ${prod.preco_sugerido ? '<span style="font-size: 0.62rem; color: #D97706; font-weight: 700;">(Sugerido)</span>' : ''}
            </div>
          </div>

          <!-- Row 2: Product Title (Full 100% width with 2-line clamp support) -->
          <div style="width: 100%; min-width: 0;">
            <h4 style="margin: 0; font-size: 0.94rem; font-weight: 800; color: var(--text-main); line-height: 1.35; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; word-break: break-word;" title="${escapeHTML(prod.descricao || 'Produto sem descrição')}">
              ${escapeHTML(prod.descricao || 'Produto sem descrição')}
            </h4>
          </div>

          <!-- Row 3: Action Buttons Bar -->
          <div style="display: flex; justify-content: flex-end; align-items: center; gap: 0.4rem; padding-top: 0.35rem; border-top: 1px solid var(--border-color); margin-top: 0.1rem;">
            <button class="btn-quick-add-list btn btn-secondary" data-id="${prod.id}" title="Adicionar à Lista de Compras" style="padding: 0.25rem 0.6rem; font-size: 0.72rem; font-weight: 800; border-radius: var(--radius-pill); display: inline-flex; align-items: center; gap: 0.2rem; cursor: pointer; white-space: nowrap;">
              <span class="material-symbols-outlined" style="font-size: 14px; color: var(--color-primary-text, #2563EB);">add_shopping_cart</span>
              + Lista
            </button>

            <button class="btn-delete-ai-prod" data-id="${prod.id}" title="Excluir do catálogo" style="background: none; border: none; color: var(--text-muted); width: 26px; height: 26px; display: flex; align-items: center; justify-content: center; cursor: pointer;">
              <span class="material-symbols-outlined" style="font-size: 16px;">delete</span>
            </button>
          </div>
        </div>
      `;
      }).join('');

      // Bind delete buttons
      listContainer.querySelectorAll('.btn-delete-ai-prod').forEach(btn => {
        btn.addEventListener('click', async (e) => {
          e.preventDefault();
          e.stopPropagation();
          const id = btn.getAttribute('data-id');
          if (id) {
            await deleteProdutoScanned(id);
            syncManager.showToast('Item removido com sucesso.', 'info');
            await refreshScannedList();
          }
        });
      });

      // Bind quick add to list buttons
      listContainer.querySelectorAll('.btn-quick-add-list').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const id = btn.getAttribute('data-id');
          const prod = cachedScannedProducts.find(p => String(p.id) === String(id));
          if (prod) {
            openQuickAddModal(prod);
          }
        });
      });

      // Bind card click to re-open modal for editing
      listContainer.querySelectorAll('.scanned-ai-card').forEach(card => {
        card.addEventListener('click', (e) => {
          if (e.target.closest('.btn-delete-ai-prod') || e.target.closest('.btn-quick-add-list')) return;
          const id = card.getAttribute('data-id');
          const prod = cachedScannedProducts.find(p => String(p.id) === String(id));
          if (prod) {
            const numPrice = typeof prod.rawPrecoGondola === 'number'
              ? prod.rawPrecoGondola
              : (typeof prod.preco === 'number' ? prod.preco : (parseFloat(prod.precoGondola?.replace(/[^\d,]/g, '').replace(',', '.')) || 0));

            openResultModal({
              ean: prod.barcode || prod.ean || '',
              descricao: prod.descricao || '',
              descricao_bruta: prod.descricao_bruta || '',
              preco: numPrice,
              preco_sugerido: !!prod.preco_sugerido,
              peso_ou_quantidade: prod.peso_ou_quantidade || '',
              source: prod.source || 'gemini-vision'
            });
          }
        });
      });
    } catch (e) {
      console.warn('[ScannerAIView] refreshScannedList error:', e);
    }
  }

  // Match History Prices in Local IndexedDB
  async function searchHistoryPrice(ean, desc) {
    try {
      const notas = await getNotasLocal();
      const matches = [];
      const cleanEan = (ean || '').replace(/\D/g, '');
      const searchTerms = (desc || '').toLowerCase().trim().split(/\s+/).filter(t => t.length > 2);

      for (const nota of (notas || [])) {
        if (!nota.itens) continue;
        for (const item of nota.itens) {
          let isMatch = false;
          const itemCode = (item.codigo || '').replace(/\D/g, '');

          if (cleanEan && itemCode && (itemCode === cleanEan || itemCode.endsWith(cleanEan) || cleanEan.endsWith(itemCode))) {
            isMatch = true;
          } else if (searchTerms.length > 0) {
            const itemDesc = (item.descricao || '').toLowerCase();
            const matchedTerms = searchTerms.filter(term => itemDesc.includes(term));
            if (matchedTerms.length >= Math.min(2, searchTerms.length)) {
              isMatch = true;
            }
          }

          if (isMatch) {
            matches.push({
              item,
              notaData: nota.data || nota.dataEmissao,
              emitente: nota.emitente?.nome || 'Estabelecimento',
              valorUnitario: item.valorUnitario || item.valorTotal / (item.quantidade || 1)
            });
          }
        }
      }

      // Sort by newest date
      matches.sort((a, b) => new Date(b.notaData) - new Date(a.notaData));
      activeHistoryMatches = matches;
      renderHistoryComparison();
    } catch (e) {
      console.warn('[ScannerAIView] searchHistoryPrice error:', e);
    }
  }

  function renderHistoryComparison() {
    const enteredPrice = parseCurrencyValue(inputPrice.value);

    if (!activeHistoryMatches || activeHistoryMatches.length === 0) {
      historyBadge.textContent = 'Sem compras registradas';
      historyContent.innerHTML = `
        <div style="font-size: 0.8rem; color: var(--color-muted); display: flex; align-items: center; gap: 0.35rem;">
          <span class="material-symbols-outlined" style="font-size: 16px; opacity: 0.6;">info</span>
          Este produto ainda não foi encontrado nas suas notas fiscais anteriores.
        </div>
      `;
      return;
    }

    const latest = activeHistoryMatches[0];
    const prevPrice = latest.valorUnitario;
    historyBadge.textContent = `${activeHistoryMatches.length} compras no histórico`;

    let comparisonHtml = '';
    if (enteredPrice > 0 && prevPrice > 0) {
      const diff = enteredPrice - prevPrice;
      const diffPercent = (diff / prevPrice) * 100;
      const isMoreExpensive = diff > 0.005;
      const isCheaper = diff < -0.005;

      let badgeColor = '#94a3b8';
      let badgeBg = 'rgba(255,255,255,0.08)';
      let badgeText = 'Mesmo Preço';
      let badgeIcon = 'drag_handle';

      if (isMoreExpensive) {
        badgeColor = '#f87171';
        badgeBg = 'rgba(239, 68, 68, 0.15)';
        badgeText = `+${formatBRL(diff)} (+${diffPercent.toFixed(1)}%) mais caro`;
        badgeIcon = 'trending_up';
      } else if (isCheaper) {
        badgeColor = '#4ade80';
        badgeBg = 'rgba(34, 197, 94, 0.15)';
        badgeText = `${formatBRL(diff)} (${diffPercent.toFixed(1)}%) mais barato`;
        badgeIcon = 'trending_down';
      }

      comparisonHtml = `
        <div style="display: inline-flex; align-items: center; gap: 0.35rem; background: ${badgeBg}; color: ${badgeColor}; padding: 0.25rem 0.6rem; border-radius: 0.45rem; font-size: 0.78rem; font-weight: 700; margin-top: 0.5rem;">
          <span class="material-symbols-outlined" style="font-size: 16px;">${badgeIcon}</span>
          ${badgeText}
        </div>
      `;
    }

    const dateStr = latest.notaData ? new Date(latest.notaData).toLocaleDateString('pt-BR') : 'Data recente';

    historyContent.innerHTML = `
      <div>
        <div style="display: flex; justify-content: space-between; align-items: center;">
          <span style="font-size: 0.8rem; color: var(--color-muted);">${latest.emitente} (${dateStr})</span>
          <span style="font-size: 0.95rem; font-weight: 800; color: #FFF;">Último pago: ${formatBRL(prevPrice)}</span>
        </div>
        ${comparisonHtml}
      </div>
    `;
  }

  // Regional Quotes Comparison
  async function loadRegionalComparison(ean) {
    if (!comparisonBox || !comparisonContent) return;

    if (!ean) {
      comparisonBox.style.display = 'none';
      return;
    }

    comparisonBox.style.display = 'block';
    comparisonContent.innerHTML = `<p class="text-muted" style="font-size: 0.8rem; margin: 0;">Consultando cotações da região...</p>`;

    try {
      const cotacoes = await getCotacoesByEAN(ean);
      const definedQuotes = (cotacoes || []).filter(c => c.preco > 0 && c.mercado && c.mercado !== 'Não informado');
      activeRegionalQuotes = definedQuotes;
      renderRegionalComparison();
    } catch (e) {
      console.warn('[ScannerAIView] loadRegionalComparison error:', e);
      if (comparisonBadge) comparisonBadge.textContent = '';
      comparisonContent.innerHTML = `<p class="text-muted" style="font-size: 0.8rem; margin: 0;">Sem cotações registradas para este item.</p>`;
    }
  }

  function renderRegionalComparison() {
    if (!comparisonBox || !comparisonContent) return;

    if (!activeRegionalQuotes || activeRegionalQuotes.length === 0) {
      if (comparisonBadge) comparisonBadge.textContent = 'Sem outras cotações';
      comparisonContent.innerHTML = `
        <div style="font-size: 0.78rem; color: var(--text-muted); display: flex; align-items: center; gap: 0.35rem;">
          <span class="material-symbols-outlined" style="font-size: 15px; opacity: 0.6;">info</span>
          Seja o primeiro a cotar este produto em diferentes supermercados!
        </div>
      `;
      return;
    }

    if (comparisonBadge) comparisonBadge.textContent = `${activeRegionalQuotes.length} cotações na região`;

    // Deduplicate best price per store
    const storeBestMap = new Map();
    for (const q of activeRegionalQuotes) {
      const existing = storeBestMap.get(q.mercado);
      if (!existing || q.preco < existing.preco) {
        storeBestMap.set(q.mercado, q);
      }
    }

    const storeQuotes = Array.from(storeBestMap.values()).sort((a, b) => a.preco - b.preco);
    const enteredPrice = parseCurrencyValue(inputPrice.value);
    const lowestOther = storeQuotes[0];

    let alertHtml = '';
    if (enteredPrice > 0 && lowestOther && lowestOther.preco > 0) {
      if (enteredPrice > lowestOther.preco + 0.01) {
        const saving = enteredPrice - lowestOther.preco;
        const savingPercent = ((saving / enteredPrice) * 100).toFixed(0);
        alertHtml = `
          <div style="background: rgba(239, 68, 68, 0.12); border: 1px solid rgba(239, 68, 68, 0.3); color: #dc2626; border-radius: 0.5rem; padding: 0.45rem 0.65rem; font-size: 0.76rem; font-weight: 700; margin-bottom: 0.5rem; display: flex; align-items: center; gap: 0.35rem;">
            <span class="material-symbols-outlined" style="font-size: 16px;">price_check</span>
            Mais barato no ${escapeHTML(lowestOther.mercado)} por ${formatBRL(lowestOther.preco)} (Economize ${savingPercent}%)
          </div>
        `;
      } else if (enteredPrice <= lowestOther.preco - 0.01) {
        alertHtml = `
          <div style="background: rgba(16, 185, 129, 0.12); border: 1px solid rgba(16, 185, 129, 0.3); color: #059669; border-radius: 0.5rem; padding: 0.45rem 0.65rem; font-size: 0.76rem; font-weight: 700; margin-bottom: 0.5rem; display: flex; align-items: center; gap: 0.35rem;">
            <span class="material-symbols-outlined" style="font-size: 16px;">local_fire_department</span>
            🔥 Melhor preço registrado na região!
          </div>
        `;
      }
    }

    comparisonContent.innerHTML = `
      ${alertHtml}
      <div style="display: flex; flex-direction: column; gap: 0.35rem;">
        ${storeQuotes.slice(0, 4).map((sq, idx) => {
          const dateStr = sq.createdAt || sq.created_at ? new Date(sq.createdAt || sq.created_at).toLocaleDateString('pt-BR') : '';
          return `
          <div style="display: flex; justify-content: space-between; align-items: center; font-size: 0.78rem; padding: 0.35rem 0.55rem; background: var(--bg-surface); border-radius: 0.4rem; border: 1px solid var(--border-color);">
            <div style="display: flex; align-items: center; gap: 0.35rem; min-width: 0;">
              <span style="font-weight: 800; color: ${idx === 0 ? '#10B981' : 'var(--text-muted)'}; font-size: 0.72rem; flex-shrink: 0;">#${idx + 1}</span>
              <strong style="color: var(--text-main); white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">${escapeHTML(sq.mercado)}</strong>
              ${dateStr ? `<span style="font-size: 0.68rem; color: var(--text-muted); flex-shrink: 0;">(${dateStr})</span>` : ''}
            </div>
            <div style="display: flex; align-items: center; gap: 0.45rem; flex-shrink: 0;">
              <span style="font-family: var(--font-mono); font-weight: 800; color: ${idx === 0 ? '#10B981' : 'var(--text-main)'};">${formatBRL(sq.preco)}</span>
            </div>
          </div>
        `;
        }).join('')}
      </div>
    `;
  }

  function parseCurrencyValue(str) {
    if (!str) return 0;
    const clean = str.replace(/[^\d]/g, '');
    return parseFloat(clean) / 100 || 0;
  }

  function formatCurrencyInput(val) {
    const num = parseCurrencyValue(val);
    return num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  // Open Modal with Scanned Data
  function openResultModal(data) {
    inputEan.value = data.ean || '';
    inputDesc.value = data.descricao || '';
    currentRawDesc = data.descricao_bruta || data.descricao || '';
    textRawDesc.textContent = currentRawDesc;

    if (data.preco && data.preco > 0) {
      inputPrice.value = data.preco.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    } else {
      inputPrice.value = '';
    }

    inputQty.value = data.peso_ou_quantidade || '';

    currentIsPrecoSugerido = !!data.preco_sugerido;
    const labelPriceSource = container.querySelector('#label-price-source');
    if (labelPriceSource) {
      if (currentIsPrecoSugerido) {
        labelPriceSource.innerHTML = `<span style="color: #fbbf24; font-weight: 700; display: inline-flex; align-items: center; gap: 0.2rem;"><span class="material-symbols-outlined" style="font-size: 13px;">lightbulb</span> Sugerido pelo Mercado</span>`;
      } else {
        labelPriceSource.innerHTML = `<span style="color: #34d399; font-weight: 700; display: inline-flex; align-items: center; gap: 0.2rem;"><span class="material-symbols-outlined" style="font-size: 13px;">check_circle</span> Lido da Etiqueta</span>`;
      }
    }

    // Show/hide raw toggle
    if (data.descricao_bruta && data.descricao_bruta !== data.descricao) {
      btnToggleRawDesc.style.display = 'inline-block';
    } else {
      btnToggleRawDesc.style.display = 'none';
    }
    rawDescContainer.style.display = 'none';

    // Search historical comparison & regional live prices
    searchHistoryPrice(data.ean, data.descricao);
    loadRegionalComparison(data.ean);

    // Populate open lists in dropdown
    populateOpenLists();

    // Update buttons enabled/disabled state based on description requirement
    updateModalActionButtonsState();

    resultModal.classList.remove('hidden');

    // Auto-focus: Description first; if already filled, focus on price
    setTimeout(() => {
      const hasDesc = inputDesc.value.trim().length > 0;
      if (!hasDesc) {
        inputDesc.focus();
        inputDesc.select?.();
      } else {
        inputPrice.focus();
        inputPrice.select?.();
      }
    }, 120);
  }

  // Update modal action buttons state (description is mandatory)
  function updateModalActionButtonsState() {
    const hasDesc = inputDesc && inputDesc.value.trim().length > 0;

    if (btnSaveNext) {
      btnSaveNext.disabled = !hasDesc;
      btnSaveNext.style.opacity = hasDesc ? '1' : '0.45';
      btnSaveNext.style.cursor = hasDesc ? 'pointer' : 'not-allowed';
    }

    const listAddButtons = container.querySelectorAll('.btn-add-to-list-item');
    listAddButtons.forEach(btn => {
      btn.disabled = !hasDesc;
      btn.style.opacity = hasDesc ? '1' : '0.45';
      btn.style.cursor = hasDesc ? 'pointer' : 'not-allowed';
    });
  }

  // Populate Open Shopping Lists in Modal
  async function populateOpenLists() {
    const containerLists = container.querySelector('#open-lists-container');
    const badgeCount = container.querySelector('#open-lists-count-badge');
    if (!containerLists) return;

    try {
      const openLists = await getOpenShoppingLists();

      if (badgeCount) {
        badgeCount.textContent = `${openLists.length} aberta${openLists.length === 1 ? '' : 's'}`;
      }

      if (openLists.length === 0) {
        containerLists.innerHTML = `
          <div style="text-align: center; padding: 1rem 0.5rem;">
            <p style="margin: 0 0 0.65rem 0; color: var(--text-muted); font-size: 0.82rem;">Nenhuma lista aberta encontrada.</p>
            <button type="button" id="btn-create-open-list" class="btn btn-secondary" style="padding: 0.45rem 0.85rem; font-size: 0.78rem; font-weight: 700; border-radius: var(--radius-pill); cursor: pointer; display: inline-flex; align-items: center; gap: 0.25rem;">
              <span class="material-symbols-outlined" style="font-size: 15px;">add</span>
              Criar Lista "Minhas Compras"
            </button>
          </div>
        `;

        const btnCreate = containerLists.querySelector('#btn-create-open-list');
        if (btnCreate) {
          btnCreate.addEventListener('click', async () => {
            btnCreate.disabled = true;
            btnCreate.textContent = 'Criando...';
            const newList = await createQuickShoppingList('Minhas Compras', 'personal');
            if (newList && newList.id) {
              await populateOpenLists();
              await handleSaveProduct(true, newList.id);
            }
          });
        }
        return;
      }

      containerLists.innerHTML = openLists.map(l => {
        const isTarget = targetListId && Number(targetListId) === Number(l.id);
        const isFamily = l.scope === 'family';
        const title = l.name || l.title || l.nome || 'Lista de Compras';
        return `
          <div class="open-list-card-item" style="display: flex; align-items: center; justify-content: space-between; background: var(--bg-surface); border: 1px solid var(--border-color); border-radius: 0.65rem; padding: 0.55rem 0.75rem; gap: 0.5rem; transition: all 120ms ease;">
            <div style="flex: 1; min-width: 0; display: flex; align-items: center; gap: 0.45rem;">
              <span class="material-symbols-outlined" style="font-size: 18px; color: var(--color-primary-text, #2563EB); flex-shrink: 0;">shopping_bag</span>
              <div style="min-width: 0;">
                <div style="font-size: 0.84rem; font-weight: 700; color: var(--text-main); white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
                  ${escapeHTML(title)}
                </div>
                <div style="font-size: 0.7rem; color: var(--text-muted); display: flex; align-items: center; gap: 0.3rem;">
                  <span>${isFamily ? '👨‍👩‍👧 Família' : '👤 Pessoal'}</span>
                  ${isTarget ? '<span style="color: #10B981; font-weight: 700;">• Origem</span>' : ''}
                </div>
              </div>
            </div>
            <button type="button" class="btn-add-to-list-item btn btn-secondary" data-list-id="${l.id}" style="padding: 0.4rem 0.75rem; font-size: 0.78rem; font-weight: 800; border-radius: var(--radius-pill); display: inline-flex; align-items: center; gap: 0.25rem; flex-shrink: 0; white-space: nowrap; cursor: pointer;">
              <span class="material-symbols-outlined" style="font-size: 15px;">add</span>
              Adicionar
            </button>
          </div>
        `;
      }).join('');

      // Wire each list item add button
      containerLists.querySelectorAll('.btn-add-to-list-item').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const listId = Number(btn.getAttribute('data-list-id'));
          if (listId) {
            handleSaveProduct(true, listId);
          }
        });
      });

      // Update button states right after rendering open lists
      updateModalActionButtonsState();

    } catch (e) {
      console.warn('Erro ao carregar listas abertas:', e);
      containerLists.innerHTML = '<div style="text-align: center; padding: 0.75rem; color: var(--text-muted); font-size: 0.8rem;">Erro ao carregar listas</div>';
    }
  }

  function closeModal() {
    resultModal.classList.add('hidden');
    if (activeAIScanner) {
      activeAIScanner.unfreezePreview();
    }
    // If in continuous 1D barcode mode, restart camera stream
    if (currentScanMode === '1d-barcode' && activeAIScanner) {
      activeAIScanner.start('1d-barcode').catch(() => {});
    }
  }

  // Save Scanned Product Handler
  async function handleSaveProduct(andScanNext = false, addToListId = null) {
    const ean = inputEan.value.trim();
    const descricao = inputDesc.value.trim();
    const preco = parseCurrencyValue(inputPrice.value);
    const peso_ou_quantidade = inputQty.value.trim();

    if (!descricao) {
      syncManager.showToast('Preencha obrigatoriamente o nome do produto.', 'warning');
      inputDesc.focus();
      return;
    }

    const latestMatch = (activeHistoryMatches && activeHistoryMatches.length > 0) ? activeHistoryMatches[0] : null;
    let diff = null;
    let diffPercent = null;
    if (preco > 0 && latestMatch && latestMatch.valorUnitario > 0) {
      diff = preco - latestMatch.valorUnitario;
      diffPercent = ((diff / latestMatch.valorUnitario) * 100).toFixed(1);
    }

    const chosenMercado = activeSessionStore ? activeSessionStore.trim() : (latestMatch?.emitente || 'Não informado');
    const isPendenteEstabelecimento = !activeSessionStore && !latestMatch?.emitente;

    const itemToSave = {
      barcode: ean,
      ean,
      format: 'EAN_13',
      descricao: descricao || 'Item sem descrição',
      descricao_bruta: currentRawDesc,
      precoGondola: preco > 0 ? formatBRL(preco) : null,
      rawPrecoGondola: preco > 0 ? preco : null,
      preco: preco > 0 ? preco : null,
      preco_sugerido: currentIsPrecoSugerido,
      peso_ou_quantidade,
      ultimoPrecoPago: latestMatch?.valorUnitario || null,
      ultimoMercado: chosenMercado,
      pendente_estabelecimento: isPendenteEstabelecimento,
      ultimaDataCompra: latestMatch?.notaData || '',
      diferencaPreco: diff,
      diferencaPercent: diffPercent,
      source: 'gemini-vision',
      dataEscaneada: new Date().toISOString()
    };

    try {
      await saveProdutoScanned(itemToSave);

      // Register quotation in cotacoes table
      if (preco > 0 && ean) {
        const cotacaoPayload = {
          ean,
          descricao: descricao || 'Item escaneado',
          mercado: chosenMercado,
          preco,
          precoOriginal: preco,
          tipoFonte: 'scan_gondola',
          statusEstabelecimento: isPendenteEstabelecimento ? 'pendente' : 'definido',
          userName: authManager.getUser()?.name || authManager.getUser()?.email?.split('@')[0] || 'Usuário',
          userId: authManager.getUser()?.id || 'usr_guest_default'
        };
        await saveCotacaoLocal(cotacaoPayload);
      }

      // If adding to a specific list:
      const effectiveListId = addToListId || (targetListId && !andScanNext ? targetListId : null);
      if (effectiveListId) {
        const catInfo = categorizeItem(descricao);
        const itemPayload = {
          listId: effectiveListId,
          description: descricao || 'Produto escaneado',
          quantity: 1,
          unit: 'UN',
          estimatedPrice: preco > 0 ? preco : 0,
          actualPrice: 0,
          categoryName: catInfo?.nome || 'Outros',
          isChecked: false,
          actualQuantity: 0,
        };

        if (authManager.isAuthenticated()) {
          try {
            const res = await authManager.fetchWithAuth(`/api/listas/${effectiveListId}/itens`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(itemPayload),
            });
            if (res.ok) {
              const created = await res.json();
              await saveListaItemLocal(created);
            } else {
              await saveListaItemLocal(itemPayload);
            }
          } catch {
            await saveListaItemLocal(itemPayload);
          }
        } else {
          await saveListaItemLocal(itemPayload);
        }
      }

      await refreshScannedList();
      closeModal();

      if (effectiveListId) {
        syncManager.showToast('Item adicionado na lista! Pronto para o próximo.', 'success');
      } else {
        syncManager.showToast('Cotação guardada com sucesso!', 'success');
      }
    } catch (e) {
      console.warn('[ScannerAIView] handleSaveProduct error:', e);
      syncManager.showToast('Falha ao salvar produto.', 'error');
    }
  }

  // Setup Quick Add To List Modal Logic
  const quickListModal = container.querySelector('#ai-quick-add-list-modal');
  const btnCloseQuickList = container.querySelector('#btn-close-quick-list-modal');
  const btnCancelQuickList = container.querySelector('#btn-cancel-quick-list-modal');
  const quickListTitle = container.querySelector('#quick-list-item-title');
  const quickListPrice = container.querySelector('#quick-list-item-price');
  const quickListQty = container.querySelector('#quick-list-item-qty');
  const quickListOptions = container.querySelector('#quick-list-options-container');
  let activeQuickAddProd = null;

  async function openQuickAddModal(prod) {
    activeQuickAddProd = prod;
    quickListTitle.textContent = prod.descricao || 'Produto escaneado';
    const numPrice = typeof prod.preco === 'number' && prod.preco > 0 
      ? prod.preco 
      : (typeof prod.rawPrecoGondola === 'number' ? prod.rawPrecoGondola : 0);
    quickListPrice.textContent = numPrice > 0 ? formatBRL(numPrice) : 'Preço não definido';
    quickListQty.value = '1';

    async function renderListOptions() {
      quickListOptions.innerHTML = '<div style="text-align: center; padding: 1rem; color: var(--text-muted); font-size: 0.82rem;">Carregando listas...</div>';

      try {
        const openLists = await getOpenShoppingLists();

        if (openLists.length === 0) {
          quickListOptions.innerHTML = `
            <div style="text-align: center; padding: 1rem 0.5rem;">
              <span class="material-symbols-outlined" style="font-size: 32px; color: var(--text-muted); opacity: 0.6; display: block; margin-bottom: 0.35rem;">playlist_add</span>
              <p style="margin: 0 0 0.85rem 0; color: var(--text-muted); font-size: 0.84rem;">Nenhuma lista aberta encontrada.</p>
              <button type="button" id="btn-create-quick-list-modal" class="btn btn-primary" style="padding: 0.55rem 1rem; font-size: 0.82rem; font-weight: 800; border-radius: var(--radius-pill); display: inline-flex; align-items: center; gap: 0.35rem; cursor: pointer;">
                <span class="material-symbols-outlined" style="font-size: 16px;">add</span>
                Criar Nova Lista "Minhas Compras"
              </button>
            </div>
          `;

          const btnCreate = quickListOptions.querySelector('#btn-create-quick-list-modal');
          if (btnCreate) {
            btnCreate.addEventListener('click', async () => {
              btnCreate.disabled = true;
              btnCreate.textContent = 'Criando lista...';
              const newList = await createQuickShoppingList('Minhas Compras', 'personal');
              if (newList && newList.id) {
                await addProdToTargetList(newList.id);
              }
            });
          }
        } else {
          quickListOptions.innerHTML = `
            ${openLists.map(l => `
              <button type="button" class="btn-select-target-list" data-list-id="${l.id}" style="display: flex; align-items: center; justify-content: space-between; padding: 0.65rem 0.85rem; border: 1px solid var(--border-color); border-radius: 0.65rem; background: var(--bg-surface); cursor: pointer; text-align: left; transition: all 120ms ease; gap: 0.5rem; width: 100%;">
                <div style="min-width: 0;">
                  <div style="font-weight: 700; font-size: 0.86rem; color: var(--text-main); white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
                    ${escapeHTML(l.name || l.title || l.nome || 'Lista de Compras')}
                  </div>
                  <div style="font-size: 0.7rem; color: var(--text-muted);">
                    ${l.scope === 'family' ? '👨‍👩‍👧 Família' : '👤 Pessoal'}
                  </div>
                </div>
                <span class="material-symbols-outlined" style="font-size: 18px; color: var(--color-primary-text, #2563EB);">add_circle</span>
              </button>
            `).join('')}
            <div style="margin-top: 0.4rem; text-align: center;">
              <button type="button" id="btn-create-another-quick-list" style="background: none; border: none; font-size: 0.75rem; color: var(--color-primary-text, #2563EB); font-weight: 700; cursor: pointer; display: inline-flex; align-items: center; gap: 0.2rem;">
                <span class="material-symbols-outlined" style="font-size: 15px;">add</span>
                Criar outra lista
              </button>
            </div>
          `;

          quickListOptions.querySelectorAll('.btn-select-target-list').forEach(btn => {
            btn.addEventListener('click', async () => {
              const listId = Number(btn.getAttribute('data-list-id'));
              if (listId) {
                await addProdToTargetList(listId);
              }
            });
          });

          const btnAnother = quickListOptions.querySelector('#btn-create-another-quick-list');
          if (btnAnother) {
            btnAnother.addEventListener('click', async () => {
              const title = prompt('Nome da nova lista de compras:', 'Compras');
              if (title && title.trim()) {
                const newList = await createQuickShoppingList(title.trim(), 'personal');
                if (newList && newList.id) {
                  await addProdToTargetList(newList.id);
                }
              }
            });
          }
        }
      } catch (e) {
        console.warn('Erro ao abrir modal de listas:', e);
        quickListOptions.innerHTML = '<div style="text-align: center; padding: 1rem; color: var(--text-muted); font-size: 0.82rem;">Erro ao carregar listas.</div>';
      }
    }

    async function addProdToTargetList(listId) {
      const qty = parseFloat(quickListQty.value) || 1;
      if (listId && activeQuickAddProd) {
        const catInfo = categorizeItem(activeQuickAddProd.descricao);
        const itemPayload = {
          listId,
          description: activeQuickAddProd.descricao || 'Produto escaneado',
          quantity: qty,
          unit: activeQuickAddProd.peso_ou_quantidade || 'UN',
          estimatedPrice: typeof activeQuickAddProd.rawPrecoGondola === 'number' ? activeQuickAddProd.rawPrecoGondola : (activeQuickAddProd.preco || 0),
          actualPrice: 0,
          categoryName: catInfo?.nome || 'Outros',
          barcode: activeQuickAddProd.ean || activeQuickAddProd.barcode || '',
          isChecked: false,
          actualQuantity: 0,
        };

        if (authManager.isAuthenticated()) {
          try {
            const res = await authManager.fetchWithAuth(`/api/listas/${listId}/itens`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(itemPayload),
            });
            if (res.ok) {
              const created = await res.json();
              await saveListaItemLocal(created);
            } else {
              await saveListaItemLocal(itemPayload);
            }
          } catch {
            await saveListaItemLocal(itemPayload);
          }
        } else {
          await saveListaItemLocal(itemPayload);
        }

        quickListModal.classList.add('hidden');
        syncManager.showToast('Item adicionado à lista!', 'success');
      }
    }

    quickListModal.classList.remove('hidden');
    await renderListOptions();
  }

  if (btnCloseQuickList) {
    btnCloseQuickList.addEventListener('click', () => quickListModal?.classList.add('hidden'));
  }
  if (btnCancelQuickList) {
    btnCancelQuickList.addEventListener('click', () => quickListModal?.classList.add('hidden'));
  }

  // Stepper controls for Quick Add modal in AI Scanner
  const btnQuickAIQtyMinus = container.querySelector('#btn-quick-ai-qty-minus');
  const btnQuickAIQtyPlus = container.querySelector('#btn-quick-ai-qty-plus');

  if (btnQuickAIQtyMinus && quickListQty) {
    btnQuickAIQtyMinus.addEventListener('click', () => {
      let currentVal = parseFloat(quickListQty.value) || 1;
      if (currentVal > 1) {
        currentVal = Math.max(1, currentVal - 1);
      } else if (currentVal > 0.5) {
        currentVal = 0.5;
      } else if (currentVal > 0.1) {
        currentVal = Math.max(0.1, Number((currentVal - 0.1).toFixed(1)));
      }
      quickListQty.value = currentVal;
    });
  }

  if (btnQuickAIQtyPlus && quickListQty) {
    btnQuickAIQtyPlus.addEventListener('click', () => {
      let currentVal = parseFloat(quickListQty.value) || 0;
      if (currentVal < 1) {
        currentVal = 1;
      } else {
        currentVal = Math.floor(currentVal) + 1;
      }
      quickListQty.value = currentVal;
    });
  }

  container.querySelectorAll('.btn-quick-ai-qty-preset').forEach(btn => {
    btn.addEventListener('click', () => {
      const delta = btn.getAttribute('data-delta');
      const setVal = btn.getAttribute('data-set');
      if (delta) {
        const add = parseFloat(delta) || 0;
        let currentVal = parseFloat(quickListQty.value) || 0;
        quickListQty.value = Math.max(1, currentVal + add);
      } else if (setVal) {
        quickListQty.value = setVal;
      }
    });
  });

  // Setup Scanner Instance
  activeAIScanner = new AIScanner(
    'ai-qr-reader',
    async (payload) => {
      // Barcode detected in 1D mode
      const saved = await saveProdutoScanned(payload);
      // Background AI enrichment
      enrichPendingProdutos().then(() => refreshScannedList()).catch(() => {});
      openResultModal(saved || payload);
    },
    (err) => {
      console.warn('[ScannerAIView] Camera scan error:', err);
    }
  );

  function toggleCameraPower() {
    isCameraOn = !isCameraOn;
    localStorage.setItem('scanner_ai_camera_on', String(isCameraOn));

    const icon = container.querySelector('#icon-camera-power');
    const btn = container.querySelector('#btn-toggle-camera-power');
    if (icon) {
      icon.textContent = isCameraOn ? 'videocam' : 'videocam_off';
      icon.style.color = isCameraOn ? '#10B981' : '#EF4444';
    }
    if (btn) {
      btn.title = isCameraOn ? 'Desligar Câmera' : 'Ligar Câmera';
    }

    setMode(currentScanMode);
    if (isCameraOn) {
      syncManager.showToast('Câmera ligada.', 'info');
    } else {
      syncManager.showToast('Câmera desligada.', 'info');
    }
  }

  const btnToggleCamera = container.querySelector('#btn-toggle-camera-power');
  if (btnToggleCamera) {
    btnToggleCamera.addEventListener('click', toggleCameraPower);
  }

  function showCameraBlockedState(customMsg) {
    const videoCont = container.querySelector('#ai-qr-reader');
    if (videoCont) {
      videoCont.innerHTML = `
        <div style="width: 100%; height: 100%; display: flex; flex-direction: column; align-items: center; justify-content: center; background: #0F172A; color: #94A3B8; text-align: center; padding: 1.25rem;">
          <span class="material-symbols-outlined" style="font-size: 38px; margin-bottom: 0.35rem; color: #F59E0B;">no_photography</span>
          <span style="font-size: 0.9rem; font-weight: 800; color: #F8FAFC;">Câmera Indisponível</span>
          <span style="font-size: 0.75rem; margin-top: 0.2rem; color: #94A3B8; max-width: 250px; line-height: 1.35;">${customMsg || 'Não foi possível acessar a câmera do aparelho.'}</span>
          <div style="display: flex; gap: 0.5rem; margin-top: 0.75rem;">
            <button type="button" id="btn-retry-ai-camera" style="background: var(--color-primary, #2563EB); color: #FFF; border: none; border-radius: var(--radius-pill); font-size: 0.75rem; font-weight: 700; padding: 0.4rem 0.85rem; cursor: pointer; display: inline-flex; align-items: center; gap: 0.3rem;">
              <span class="material-symbols-outlined" style="font-size: 16px;">refresh</span>
              <span>Tentar Novamente</span>
            </button>
          </div>
        </div>
      `;
      const btnRetry = videoCont.querySelector('#btn-retry-ai-camera');
      if (btnRetry) {
        btnRetry.addEventListener('click', () => {
          isCameraOn = true;
          setMode(currentScanMode);
        });
      }
    }
  }

  // Scan Mode Switcher (Label AI vs 1D Barcode)
  function setMode(mode) {
    currentScanMode = mode;
    const banner = container.querySelector('#scanner-instruction-text');
    const shutterBar = container.querySelector('.camera-shutter-bar');
    const controls = container.querySelector('#camera-controls');
    const videoCont = container.querySelector('#ai-qr-reader');

    if (!isCameraOn) {
      if (activeAIScanner) activeAIScanner.stop();
      if (banner) banner.textContent = 'Câmera desligada • Modo manual';
      if (controls) controls.style.display = 'none';
      if (videoCont) {
        videoCont.innerHTML = `
          <div style="width: 100%; height: 100%; display: flex; flex-direction: column; align-items: center; justify-content: center; background: #0F172A; color: #94A3B8; text-align: center; padding: 1rem;">
            <span class="material-symbols-outlined" style="font-size: 38px; opacity: 0.5; margin-bottom: 0.35rem; color: #94A3B8;">videocam_off</span>
            <span style="font-size: 0.88rem; font-weight: 800; color: #E2E8F0;">Câmera Desligada</span>
            <span style="font-size: 0.75rem; margin-top: 0.15rem; color: #64748B;">Modo manual ativo</span>
            <button type="button" id="btn-turn-on-ai-camera" style="margin-top: 0.65rem; background: var(--color-primary, #2563EB); color: #FFF; border: none; border-radius: var(--radius-pill); font-size: 0.75rem; font-weight: 700; padding: 0.4rem 0.95rem; cursor: pointer; display: inline-flex; align-items: center; gap: 0.3rem;">
              <span class="material-symbols-outlined" style="font-size: 16px;">videocam</span>
              <span>Ligar Câmera</span>
            </button>
          </div>
        `;
        const btnOn = videoCont.querySelector('#btn-turn-on-ai-camera');
        if (btnOn) btnOn.addEventListener('click', toggleCameraPower);
      }
      return;
    }

    if (videoCont && (videoCont.querySelector('#btn-turn-on-ai-camera') || videoCont.querySelector('#btn-retry-ai-camera'))) {
      videoCont.innerHTML = '';
    }

    if (mode === 'ai-label') {
      tabModeAI.classList.add('active');
      tabMode1D.classList.remove('active');
      if (banner) banner.textContent = 'Enquadre a etiqueta de preço e clique em Capturar';
      if (shutterBar) shutterBar.style.display = 'flex';
      if (controls) controls.style.display = 'flex';
      if (activeAIScanner) {
        activeAIScanner.start('ai-label').catch(err => {
          console.warn('[ScannerAIView] Camera start error:', err);
          showCameraBlockedState('Não foi possível iniciar a câmera para leitura de etiquetas.');
        });
      }
    } else {
      tabMode1D.classList.add('active');
      tabModeAI.classList.remove('active');
      if (banner) banner.textContent = 'Aponte a câmera para o código de barras (1D)';
      if (shutterBar) shutterBar.style.display = 'none';
      if (controls) controls.style.display = 'none';

      if (activeAIScanner) {
        activeAIScanner.start('1d-barcode').catch(err => {
          console.warn('[ScannerAIView] 1D Camera start error:', err);
          showCameraBlockedState('Não foi possível iniciar a câmera para código de barras.');
        });
      }
    }
  }

  tabModeAI.addEventListener('click', () => setMode('ai-label'));
  tabMode1D.addEventListener('click', () => setMode('1d-barcode'));

  // Start initial mode
  setMode('ai-label');

  let currentAbortController = null;

  // Cancel AI Progress button handler
  const btnCancelProgress = container.querySelector('#btn-cancel-ai-progress');
  if (btnCancelProgress) {
    btnCancelProgress.addEventListener('click', () => {
      if (currentAbortController) {
        currentAbortController.abort();
        currentAbortController = null;
      }
      hideProgress();
      if (activeAIScanner) {
        activeAIScanner.unfreezePreview();
      }
      syncManager.showToast('Identificação cancelada.', 'info');
    });
  }

  // Capture Button Action
  btnCaptureFrame.addEventListener('click', async () => {
    if (!activeAIScanner) return;

    let frameBlob = null;
    try {
      frameBlob = await activeAIScanner.captureFrame();
    } catch (captureErr) {
      console.warn('Direct frame capture failed:', captureErr);
      showCameraBlockedState('Não foi possível capturar o quadro da câmera.');
      syncManager.showToast('Câmera indisponível para captura direta.', 'error');
      return;
    }

    try {
      showProgress('Identificando produto...', 'Lendo informações da etiqueta');
      currentAbortController = new AbortController();
      const aiResult = await activeAIScanner.analyzeWithGemini(frameBlob, (step) => {
        showProgress('Identificando produto...', typeof step === 'string' ? step : 'Processando imagem...');
      }, currentAbortController.signal);

      hideProgress();
      currentAbortController = null;
      openResultModal(aiResult);
    } catch (apiErr) {
      hideProgress();
      currentAbortController = null;
      if (activeAIScanner) {
        activeAIScanner.unfreezePreview();
      }
      const userMsg = formatAIErrorMessage(apiErr);
      if (userMsg) {
        syncManager.showToast(userMsg, 'error');
      }
    }
  });

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      showProgress('Carregando foto...', 'Lendo imagem da etiqueta');
      if (activeAIScanner) {
        try {
          const url = URL.createObjectURL(file);
          activeAIScanner.freezePreview(url);
        } catch (e) {}
      }
      currentAbortController = new AbortController();
      const aiResult = await activeAIScanner.analyzeWithGemini(file, (step) => {
        showProgress('Identificando produto...', typeof step === 'string' ? step : 'Processando imagem...');
      }, currentAbortController.signal);
      hideProgress();
      currentAbortController = null;
      openResultModal(aiResult);
    } catch (err) {
      hideProgress();
      currentAbortController = null;
      if (activeAIScanner) {
        activeAIScanner.unfreezePreview();
      }
      const userMsg = formatAIErrorMessage(err);
      if (userMsg) {
        syncManager.showToast(userMsg, 'error');
      }
    } finally {
      if (cameraInput) cameraInput.value = '';
      if (galleryInput) galleryInput.value = '';
    }
  };

  if (cameraInput) cameraInput.addEventListener('change', handleFileSelected);
  if (galleryInput) galleryInput.addEventListener('change', handleFileSelected);
  if (btnUploadFile) btnUploadFile.addEventListener('click', () => galleryInput?.click());

  // Zoom Buttons Action
  container.querySelectorAll('.zoom-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      container.querySelectorAll('.zoom-btn').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      const zoom = parseFloat(btn.getAttribute('data-zoom') || '1.8');
      activeAIScanner.setZoom(zoom);
    });
  });

  // Torch Button Action
  const btnTorch = container.querySelector('#btn-torch-ai');
  if (btnTorch) {
    btnTorch.addEventListener('click', async () => {
      const state = await activeAIScanner.toggleTorch();
      btnTorch.style.color = state ? '#60a5fa' : '#FFFFFF';
      btnTorch.style.borderColor = state ? '#60a5fa' : 'rgba(255,255,255,0.25)';
    });
  }

  // Price Input Mask and Live Re-calculation
  inputPrice.addEventListener('input', (e) => {
    currentIsPrecoSugerido = false;
    const labelPriceSource = container.querySelector('#label-price-source');
    if (labelPriceSource) {
      labelPriceSource.innerHTML = `<span style="color: #60a5fa; font-weight: 700;">Editado Manualmente</span>`;
    }
    const clean = e.target.value.replace(/\D/g, '');
    const num = parseFloat(clean) / 100 || 0;
    e.target.value = num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    renderHistoryComparison();
    renderRegionalComparison();
  });

  // Description Input Listener to dynamically enable/disable buttons
  ['input', 'change', 'keyup'].forEach(evt => {
    inputDesc.addEventListener(evt, () => {
      updateModalActionButtonsState();
    });
  });

  // Raw Description Toggle
  btnToggleRawDesc.addEventListener('click', () => {
    if (rawDescContainer.style.display === 'none') {
      rawDescContainer.style.display = 'block';
      btnToggleRawDesc.textContent = 'Ocultar texto bruto';
    } else {
      rawDescContainer.style.display = 'none';
      btnToggleRawDesc.textContent = 'Ver texto bruto da etiqueta';
    }
  });

  // Back Button Action with Session Gamification Feedback
  const btnBackAI = container.querySelector('#btn-back-scanner-ai');
  if (btnBackAI) {
    btnBackAI.addEventListener('click', () => {
      if (sessionScannedCount > 0) {
        syncManager.showToast(`🎉 Você cotou ${sessionScannedCount} itens nesta sessão! Obrigado por colaborar.`, 'success');
      }
      window.location.hash = originHash;
    });
  }

  // Modal Action Buttons
  btnCloseModal.addEventListener('click', closeModal);

  // Voltar (retorna para a tela de origem)
  btnCancelModal.addEventListener('click', () => {
    closeModal();
    if (sessionScannedCount > 0) {
      syncManager.showToast(`🎉 Você cotou ${sessionScannedCount} itens nesta sessão!`, 'success');
    }
    window.location.hash = originHash;
  });

  // Guardar cotação e continuar
  btnSaveNext.addEventListener('click', () => handleSaveProduct(true));

  // Initialize view
  renderStorePills();
  refreshScannedList();
  populateOpenLists();
}

export function stopActiveAIScanner() {
  if (activeAIScanner) {
    try {
      activeAIScanner.stop();
    } catch (e) {
      console.warn('[ScannerAIView] Error stopping camera:', e);
    }
    activeAIScanner = null;
  }
}
